"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectDateFilterConfigValidationWarnings = exports.selectEffectiveDateFilterMode = exports.selectEffectiveDateFilterTitle = exports.selectEffectiveDateFilterAvailableGranularities = exports.selectEffectiveDateFilterOptions = exports.selectEffectiveDateFilterConfig = exports.selectDateFilterConfigOverrides = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2022 GoodData Corporation
var toolkit_1 = require("@reduxjs/toolkit");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var dateFilterConfigConverters_1 = require("../../../_staging/dateFilterConfig/dateFilterConfigConverters");
var renderModeSelectors_1 = require("../renderMode/renderModeSelectors");
var selectSelf = toolkit_1.createSelector(function (state) { return state; }, function (state) { return state.dateFilterConfig; });
/**
 * Returns date filter config that is specified on the loaded dashboard.
 *
 * The dashboard-level date filter configuration MAY contain overrides and additional configuration to apply
 * on top of the workspace-level date filter config. If the dashboard-level overrides are not specified, then
 * the workspace-level config should be taken as-is.
 *
 * @remarks See {@link selectEffectiveDateFilterConfig} - you can use this selector to obtain the effective
 *  date filter config that contains the final config obtained by merging the workspace-level config and the
 *  dashboard-level overrides.
 *
 * @alpha
 */
exports.selectDateFilterConfigOverrides = toolkit_1.createSelector(selectSelf, function (dateFilterConfigState) {
    var _a;
    return (_a = dateFilterConfigState.dateFilterConfig) !== null && _a !== void 0 ? _a : undefined;
});
/**
 * Returns effective date filter config. The effective date filter config is created by merging the workspace-level
 * date filter config and the dashboard-level date filter config.
 *
 * This is the configuration that the DateFilter SHOULD use when rendering filtering presets.
 *
 * @alpha
 */
exports.selectEffectiveDateFilterConfig = toolkit_1.createSelector(selectSelf, function (dateFilterConfigState) {
    ts_invariant_1.default(dateFilterConfigState.effectiveDateFilterConfig, "attempting to access uninitialized date filter config state");
    return dateFilterConfigState.effectiveDateFilterConfig;
});
/**
 * Returns effective date filter options. This is created by merging the workspace-level
 * date filter config and the dashboard-level date filter config.
 *
 * These are the date filter options that the DateFilter SHOULD use when rendering filtering presets.
 *
 * @alpha
 */
exports.selectEffectiveDateFilterOptions = toolkit_1.createSelector(exports.selectEffectiveDateFilterConfig, function (effectiveDateFilterConfig) {
    return dateFilterConfigConverters_1.convertDateFilterConfigToDateFilterOptions(effectiveDateFilterConfig);
});
/**
 * Returns effective date filter options from. This is created by merging the workspace-level
 * date filter config and the dashboard-level date filter config.
 *
 * These are the date filter options that the DateFilter SHOULD use when rendering filtering presets.
 *
 * @alpha
 */
exports.selectEffectiveDateFilterAvailableGranularities = toolkit_1.createSelector(exports.selectEffectiveDateFilterConfig, function (effectiveDateFilterConfig) { var _a, _b; return (_b = (_a = effectiveDateFilterConfig.relativeForm) === null || _a === void 0 ? void 0 : _a.availableGranularities) !== null && _b !== void 0 ? _b : []; });
/**
 * Indicates whether the effective date filter is using dashboard-level overrides.
 */
var effectiveDateFilterConfigIsUsingOverrides = toolkit_1.createSelector(selectSelf, function (dateFilterConfigState) {
    ts_invariant_1.default(dateFilterConfigState.isUsingDashboardOverrides !== undefined, "attempting to access uninitialized date filter config state");
    return dateFilterConfigState.isUsingDashboardOverrides;
});
/**
 * Returns custom title to use for the date filter. Custom title comes from the dashboard-level date filter config overrides. If no overrides
 * were defined OR the effective date filter config is not using them (because applying them means the final date filter config is invalid),
 * then no custom filter should be used.
 *
 * @alpha
 */
exports.selectEffectiveDateFilterTitle = toolkit_1.createSelector(effectiveDateFilterConfigIsUsingOverrides, exports.selectDateFilterConfigOverrides, function (isUsingOverrides, dashboardOverrides) {
    if (!isUsingOverrides) {
        return undefined;
    }
    // If this happens then the load command handler does not work correctly - it sets that the overrides are used
    // while there are none.
    ts_invariant_1.default(dashboardOverrides !== undefined);
    return dashboardOverrides.filterName;
});
/**
 * Returns display mode for the effective date filter. This always comes from the dashboard-level date filter config overrides - regardless whether
 * the rest of the overrides are actually used.
 *
 * @alpha
 */
exports.selectEffectiveDateFilterMode = toolkit_1.createSelector(renderModeSelectors_1.selectIsInEditMode, exports.selectDateFilterConfigOverrides, function (isInEditMode, dashboardOverrides) {
    var _a;
    if (isInEditMode) {
        return "active";
    }
    return (_a = dashboardOverrides === null || dashboardOverrides === void 0 ? void 0 : dashboardOverrides.mode) !== null && _a !== void 0 ? _a : "active";
});
/**
 * Returns the date filter config validation result warnings indicating any problems encountered during the date filter config resolution.
 *
 * @alpha
 */
exports.selectDateFilterConfigValidationWarnings = toolkit_1.createSelector(selectSelf, function (dateFilterConfigState) {
    var _a;
    return (_a = dateFilterConfigState.dateFilterConfigValidationWarnings) !== null && _a !== void 0 ? _a : [];
});
//# sourceMappingURL=dateFilterConfigSelectors.js.map